import { useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, ShieldCheck, User as UserIcon } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { GoogleIcon } from "@/components/icons/GoogleIcon";
import SprintCapacity from "@/components/users/SprintCapacity";
import { UserRoleBadge } from "@/components/layout/UserRoleBadge";
import { useAuth } from "@/context/AuthContext";
import {
	getDisplayName,
	getInitials,
	getProviderLabel,
	getRoleLabel,
	getSsoProvider,
} from "@/lib/user-display";

export function UserNav() {
	const { user, logout } = useAuth();
	const navigate = useNavigate();

	const displayName = getDisplayName(user);
	const initials = getInitials(user);
	const roleLabel = getRoleLabel(user);
	const ssoProvider = getSsoProvider(user);

	async function handleLogout() {
		await logout();
		navigate("/login");
	}

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button
					className="flex items-center gap-2 rounded-md p-1 sm:py-1 sm:px-2 sm:border sm:border-border/50 transition-colors hover:bg-muted-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
					aria-label={
						roleLabel
							? `Open account menu, signed in as ${displayName}, ${roleLabel}`
							: "Open account menu"
					}
				>
					<Avatar className="h-7 w-7">
						{user?.avatar_url && (
							<AvatarImage src={user.avatar_url} alt={displayName} />
						)}
						<AvatarFallback className="text-[10px]">
							{initials}
						</AvatarFallback>
					</Avatar>
					<div className="hidden min-w-0 leading-none text-left lg:block">
						<div className="truncate text-xs font-medium text-foreground">
							{displayName}
						</div>
						{roleLabel && (
							<div className="mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground">
								{ssoProvider === "google" ? (
									<GoogleIcon className="h-2.5 w-2.5 shrink-0" />
								) : (
									<ShieldCheck className="h-2.5 w-2.5 shrink-0" />
								)}
								<span className="truncate">{roleLabel}</span>
							</div>
						)}
					</div>
					<ChevronDown className="hidden h-3 w-3 text-muted-foreground sm:block" />
				</button>
			</DropdownMenuTrigger>

			<DropdownMenuContent align="end" className="w-56">
				<DropdownMenuLabel className="px-3 py-2 flex flex-col items-center justify-center">
					<div className="my-2">
						<Avatar className="h-10 w-10">
							{user?.avatar_url && (
								<AvatarImage
									src={user.avatar_url}
									alt={displayName}
								/>
							)}
							<AvatarFallback className="text-[10px]">
								{initials}
							</AvatarFallback>
						</Avatar>
					</div>
					<p className="text-sm font-bold text-primary">{displayName}</p>
					<p className="text-xs font-light text-muted-foreground break-all text-center">
						{user?.email}
					</p>
					<UserRoleBadge user={user} className="mt-2" />
					{ssoProvider && (
						<span className="mt-1.5 flex items-center gap-1 text-[10px] text-muted-foreground">
							{ssoProvider === "google" ? (
								<GoogleIcon className="h-3 w-3" />
							) : (
								<ShieldCheck className="h-3 w-3" />
							)}
							Signed in with {getProviderLabel(ssoProvider)}
						</span>
					)}
					<SprintCapacity userId={user?.id} />
				</DropdownMenuLabel>

				<DropdownMenuSeparator />
				<DropdownMenuItem
					className="gap-2 px-3"
					onSelect={() => navigate("/profile")}
				>
					<UserIcon className="h-4 w-4 text-muted-foreground" />
					Profile
				</DropdownMenuItem>

				<DropdownMenuSeparator />
				<DropdownMenuItem
					className="gap-2 px-3 text-danger focus:text-danger focus:bg-danger/10"
					onSelect={handleLogout}
				>
					<LogOut className="h-4 w-4" />
					Logout
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

export default UserNav;
